// Lib imports
import React, { useRef } from 'react';
import { Box, TextInput, Anchor, Button, Text, Heading, CheckBox } from 'grommet';
import { CircleQuestion } from 'grommet-icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

// Core imports
import PasswordInput from 'granite-admin/core/components/PasswordInput';
import FormField from 'granite-admin/core/components/FormField';
import LOGIN_MESSAGES from 'granite-admin/messages/login';
import Tooltip from 'granite-admin/core/components/Tooltip';
import Select from 'granite-admin/core/components/Select';
import { setCookie, getCookie, deleteCookie } from 'granite-admin/utils/storage';
const ssoUrl = process.env.REACT_APP_SSO_URL || null;
const showPrivacyPolicy = process.env.REACT_APP_SHOW_LOGIN_PRIVACY_POLICY === 'true' ? true : false;
const privacyPolicyLink = process.env.REACT_APP_LOGIN_PRIVACY_POLICY_LINK;
const registerDisabled = process.env.REACT_APP_REGISTER_DISABLED || false;
const getEmailQuery = async inputRef => {
  let value = inputRef?.current?.value;
  if (!value) return '';
  let emailValidity = Yup.string().email();
  let isEmailValid = await emailValidity.isValid(value);
  if (isEmailValid) return `email=${value}`;
  return '';
};
const LoginForm = ({
  eventEmitter,
  organisation,
  setOrganisation,
  loginClicked,
  config,
  msg
}) => {
  const validationSchema = Yup.object().shape({
    email: Yup.string().min(2, LOGIN_MESSAGES.EMAIL_TOO_SHORT).max(50, LOGIN_MESSAGES.EMAIL_TOO_LONG).email(LOGIN_MESSAGES.EMAIL_INVALID).required(LOGIN_MESSAGES.EMAIL_REQUIRED),
    password: Yup.string().required(LOGIN_MESSAGES.PASS_REQUIRED),
    ...(organisation?.length && {
      organisation_id: Yup.number().required(LOGIN_MESSAGES.ORGANIZATION_ID_REQUIRED)
    })
  });
  const {
    ProjectLoginForm
  } = config?.loginDetails;
  const onSubmit = async (values, {
    setSubmitting,
    setErrors
  }) => {
    const {
      isChecked,
      ...rest
    } = values;
    setErrors({});
    setSubmitting(true);
    try {
      await loginClicked(eventEmitter, rest, config);
      if (isChecked) {
        setCookie('email', values.email, 300);
        setCookie('password', values.password, 300);
        setCookie('isChecked', isChecked, 300);
      } else {
        deleteCookie('email');
        deleteCookie('password');
        deleteCookie('isChecked');
      }
    } catch (e) {
      if (e.errors) {
        setErrors(e.errors);
      }
    }
    setSubmitting(false);
  };
  const inputRef = useRef();
  const history = useHistory();
  const handleForgotPassword = async () => {
    const query = await getEmailQuery(inputRef);
    history.push(`/forgot-password?${query}`);
  };
  return /*#__PURE__*/React.createElement(Formik, {
    initialValues: {
      email: getCookie('email'),
      password: getCookie('password'),
      organisation_id: '',
      isChecked: getCookie('isChecked')
    },
    validationSchema: validationSchema,
    onSubmit: onSubmit
  }, ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue
  }) => ProjectLoginForm ? /*#__PURE__*/React.createElement(ProjectLoginForm, {
    inputRef: inputRef,
    setOrganisation: setOrganisation,
    organisation: organisation,
    history: history,
    handleForgotPassword: handleForgotPassword,
    ssoUrl: ssoUrl,
    showPrivacyPolicy: showPrivacyPolicy,
    privacyPolicyLink: privacyPolicyLink,
    registerDisabled: registerDisabled,
    config: config,
    eventEmitter: eventEmitter
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading, {
    level: 3
  }, "Sign In"), /*#__PURE__*/React.createElement(Heading, {
    level: 5,
    size: "large",
    color: "dark-2"
  }, msg), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(Text, {
    as: "div",
    color: "status-critical"
  }, errors.title), /*#__PURE__*/React.createElement(FormField, {
    name: "email",
    label: LOGIN_MESSAGES.EMAIL_LABEL,
    error: touched.email && errors.email
  }, /*#__PURE__*/React.createElement(TextInput, {
    ref: inputRef,
    type: "email",
    name: "email",
    onChange: e => {
      handleChange(e);
      setOrganisation([]);
      setFieldValue('organisation_id', '');
    },
    onBlur: handleBlur,
    value: values.email,
    style: {
      marginTop: '6px'
    }
  })), /*#__PURE__*/React.createElement(FormField, {
    name: "password",
    label: LOGIN_MESSAGES.PASS_LABEL,
    required: true,
    error: touched.password && errors.password
  }, /*#__PURE__*/React.createElement(PasswordInput, {
    name: "password",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.password,
    type: "password",
    style: {
      marginTop: '6px'
    }
  })), organisation?.length > 0 ? /*#__PURE__*/React.createElement(FormField, {
    name: "organisation_id",
    label: LOGIN_MESSAGES.ORGANIZATION_LABEL,
    error: touched.organisation_id && errors.organisation_id
  }, /*#__PURE__*/React.createElement(Select, {
    name: "organisation_id",
    options: organisation,
    labelKey: "name",
    value: values.organisation_id,
    valueKey: {
      key: 'pk',
      reduce: true
    },
    onChange: ({
      value
    }) => {
      setFieldValue('organisation_id', value);
    }
  })) : '', /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    alignSelf: "start",
    margin: {
      top: 'medium'
    },
    label: isSubmitting ? LOGIN_MESSAGES.SUBMIT_BUTTON_LABEL_SUBMITTING : LOGIN_MESSAGES.SUBMIT_BUTTON_LABEL,
    disabled: isSubmitting,
    primary: true,
    color: "secondary"
  }), privacyPolicyLink && showPrivacyPolicy === true && /*#__PURE__*/React.createElement(Box, {
    align: "start",
    margin: {
      vertical: 'medium'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    color: "#707070",
    as: "span"
  }, "By clicking ", LOGIN_MESSAGES.SUBMIT_BUTTON_LABEL.toLowerCase(), ", you are indicating that you have read and agree to the", /*#__PURE__*/React.createElement(Anchor, {
    href: privacyPolicyLink,
    target: "_blank",
    style: {
      fontSize: '12px',
      lineHeight: '16px',
      marginLeft: 5
    }
  }, "Privacy Policy"), ".")), ssoUrl && /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    align: "start",
    margin: {
      vertical: 'small'
    }
  }, /*#__PURE__*/React.createElement(Anchor, {
    onClick: () => history.push('/sso'),
    as: "span",
    label: LOGIN_MESSAGES.SSO_LINK_LABEL,
    style: {
      fontSize: '12px'
    }
  }), /*#__PURE__*/React.createElement(Tooltip, {
    border: {
      color: 'lightgray',
      size: 'xsmall'
    },
    background: "white",
    content: /*#__PURE__*/React.createElement(Text, null, "Single sign-on (SSO) is a method for authenticating users.", /*#__PURE__*/React.createElement("br", null), "With SSO,a user can use a single set of credentials to", /*#__PURE__*/React.createElement("br", null), "log into several different applications.SSO is beneficial for", /*#__PURE__*/React.createElement("br", null), "clients who want to make it easy for their users to log in,", /*#__PURE__*/React.createElement("br", null), "to reduce their overheads for password management and to ", /*#__PURE__*/React.createElement("br", null), "control password access centrally within their own organization.")
  }, /*#__PURE__*/React.createElement(CircleQuestion, {
    size: "15px",
    style: {
      marginLeft: '6px'
    }
  }))), /*#__PURE__*/React.createElement(Box, {
    align: "start",
    margin: {
      vertical: 'small'
    }
  }, /*#__PURE__*/React.createElement(Anchor, {
    onClick: handleForgotPassword,
    as: "span",
    label: LOGIN_MESSAGES.FORGOT_PASS_LINK_LABEL,
    style: {
      fontSize: '12px'
    }
  })), !registerDisabled && /*#__PURE__*/React.createElement(Box, {
    align: "start",
    margin: {
      vertical: 'small'
    }
  }, /*#__PURE__*/React.createElement(Anchor, {
    onClick: () => history.push('/register'),
    as: "span",
    label: LOGIN_MESSAGES.REGISTER_LINK_LABEL,
    style: {
      fontSize: '12px'
    }
  })))));
};
LoginForm.propTypes = {
  eventEmitter: PropTypes.object,
  msg: PropTypes.string,
  config: PropTypes.object,
  loginClicked: PropTypes.func,
  organisation: PropTypes.array,
  setOrganisation: PropTypes.func
};
export default LoginForm;